import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Modal, Box, Typography, LinearProgress } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { hideWelcome } from '../../redux/slices/Welcome/welcomeSlice'
import backgroundTrees from '../../assets/images/1.webp'
import logoRight from '../../assets/images/unl/logo-unl-white.png'
import logoLeft from '../../assets/images/unl/logo-arbol-ec.png'

const MIN_DISPLAY_MS = 1500
const FAILSAFE_MS = 7000

const WelcomeOverlay = () => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const { show, mapDataReady } = useSelector(state => state.welcome)

  const [minElapsed, setMinElapsed] = useState(false)
  const timerRef = useRef(null)
  const failsafeRef = useRef(null)

  // Timer mínimo de visualización + failsafe
  useEffect(() => {
    if (!show) {
      setMinElapsed(false)
      clearTimeout(timerRef.current)
      clearTimeout(failsafeRef.current)
      return
    }
    timerRef.current = setTimeout(() => setMinElapsed(true), MIN_DISPLAY_MS)
    failsafeRef.current = setTimeout(() => dispatch(hideWelcome()), FAILSAFE_MS)
    return () => {
      clearTimeout(timerRef.current)
      clearTimeout(failsafeRef.current)
    }
  }, [show, dispatch])

  // Ocultar cuando el tiempo mínimo ya pasó y los datos del mapa están listos
  useEffect(() => {
    if (show && minElapsed && mapDataReady) {
      dispatch(hideWelcome())
    }
  }, [show, minElapsed, mapDataReady, dispatch])

  if (!show) return null

  const loadingTextStyle = {
    fontWeight: 700,
    fontFamily: theme.typography.fontFamily,
    fontSize: 'clamp(1rem, 1.5vw, 2.5rem)',
    color: theme.palette.primary.main,
    textShadow: '0 0 10px #4e6a5b, 0 0 10px #E0DED1',
  }

  return (
    <Modal
      open={true}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <>
        {/* Fondo */}
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 0,
            backgroundImage: `url(${backgroundTrees})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        />

        {/* Logos */}
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            px: { xs: 2, sm: 4, md: 6 },
            pt: { xs: 2, sm: 3 },
            zIndex: 2,
          }}>
          <Box
            component="img"
            src={logoLeft}
            alt="Logo ÁrbolEc"
            sx={{
              mt: { xs: 2, sm: 3, md: 4, lg: 4, xl: 5 },
              ml: 1,
              height: { xs: 45, sm: 55, md: 75, lg: 85, xl: 120 },
              maxWidth: '40vw',
              objectFit: 'contain',
            }}
          />
          <Box
            component="img"
            src={logoRight}
            alt="Logo UNL"
            sx={{
              height: { xs: 80, sm: 100, md: 130, lg: 150, xl: 195 },
              maxWidth: '40vw',
              objectFit: 'contain',
            }}
          />
        </Box>

        {/* Tarjeta central */}
        <Box
          sx={{
            borderRadius: '5px',
            padding: '20px 20px',
            textAlign: 'center',
            width: 'auto',
            minWidth: '220px',
            position: 'relative',
            zIndex: 1,
            backgroundColor: '#ffffff96',
            color: 'white',
            boxShadow: '0 0 20px 5px rgba(41, 49, 29, 0.4)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}>
          <Typography
            sx={{
              ...loadingTextStyle,
              textShadow: '2px 2px 4px rgba(255, 255, 255, 0.7)',
            }}>
            Bienvenido a
          </Typography>

          <Box sx={{ width: '100%', mb: 1 }}>
            <LinearProgress
              sx={{
                height: 4,
                borderRadius: 5,
                '& .MuiLinearProgress-bar': {
                  backgroundColor: theme.palette.text.darkBlue,
                },
                backgroundColor: theme.palette.text.ivory,
              }}
            />
          </Box>

          <Typography
            sx={{
              ...loadingTextStyle,
              fontSize: 'clamp(1rem, 2vw, 2.5rem)',
              fontWeight: 700,
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.7)',
            }}>
            <Box component="span" sx={{ color: theme.palette.text.white }}>
              Árbol
            </Box>
            <Box component="span" sx={{ color: theme.palette.secondary.light }}>
              Ec
            </Box>
          </Typography>
        </Box>
      </>
    </Modal>
  )
}

export default WelcomeOverlay
